<template>
    <div class="panel panel-default">
        <div class="panel-heading">
            <div class="row">
                <div class="col-xs-18">
                    <h5>
                        Ad Revenue
                    </h5>
                </div>

                <div class="col-xs-6">
                    <select-widget
                            v-if="!loadingPortals"
                            v-model="portal"
                            class="form-control input-sm pull-right"
                            :nullable="true"
                            placeholder="(all portals)"
                            :options="portals.map(portal => ({value: portal.id, label: portal.attributes.name}))"
                    ></select-widget>
                </div>
            </div>
        </div>

        <loading-spinner v-if="loading" :style="loadingSpinnerStyle"></loading-spinner>

        <div v-else class="panel-body">
            <canvas width="400" height="200"></canvas>
        </div>

        <ad-revenue-breakdown-modal :company="company.data.id" :year="selected" :portal="portal"/>
    </div>
</template>

<script>
    const totalizer = {
        id: 'totalizer',

        beforeUpdate: chart => {
            let totals = {}
            let utmost = 0

            chart.data.datasets.forEach((dataset, datasetIndex) => {
                if (chart.isDatasetVisible(datasetIndex)) {
                    utmost = datasetIndex

                    dataset.data.forEach((value, index) => {
                        totals[index] = (totals[index] || 0) + value
                    })
                }
            })

            chart.$totalizer = {
                totals: totals,
                utmost: utmost
            }
        }
    }

    import datalabels from "chartjs-plugin-datalabels";
    import http from '@/http'
    import moment from 'moment'
    import Chart from 'chart.js'
    import AdRevenueBreakdownModal from '@/pages/Companies/Companies/Edit/Modals/AdRevenueBreakdown'
    import DateWidget from '@/vendor/forms/FormGroups/Widgets/Date'
    import SaveHeight from '@/mixins/SaveHeight'
    import SelectWidget from '@/vendor/forms/FormGroups/Widgets/Select'
    import LoadingSpinner from '@/components/Layout/LoadingSpinner'

    export default {
        components: {AdRevenueBreakdownModal, DateWidget, LoadingSpinner, SelectWidget},

        mixins: [SaveHeight],

        props: ['company'],

        data() {
            return {
                loading: true,
                loadingPortals: true,

                portal: null,
                selected: null,

                rows: null,
                portals: null
            }
        },

        mounted() {
            this.load()

            this.loadPortals()
        },

        watch: {
            loading(value) {
                if (!value) {
                    this.$nextTick(this.draw)
                }
            },

            portal() {
                this.load()
            }
        },

        computed: {
            datasets() {
                return Object.values(this.rows
                    .reduce((datasets, row) => {
                        if (!datasets.hasOwnProperty(row.channel_type + '-' + row.channel_id)) {
                            datasets[row.channel_type + '-' + row.channel_id] = {
                                label: row.channel_name,
                                data: this.years.reduce((result, year) => {
                                    result[year.format('YYYY')] = 0

                                    return result
                                }, {}),
                                backgroundColor: row.channel_color
                            }
                        }

                        datasets[row.channel_type + '-' + row.channel_id].data[row.year] = row.ad_revenue

                        return datasets
                    }, {}))
                    .map(dataset => {
                        dataset.data = Object.values(dataset.data)

                        return dataset
                    })
            },

            column() {
                return this.rows.reduce((result, row) => {
                    result[row.year] = row.ad_revenue

                    return result
                }, {})
            },

            years() {
                let startDate = moment().subtract(8, 'year').startOf('year')

                let endDate = moment().add(1, 'year').endOf('year')

                let result = []

                while (startDate.isBefore(endDate)) {
                    result.push(startDate.clone())

                    startDate.add(1, 'year')
                }

                return result
            },

            labels() {
                return this.years.map(year => year.format('YYYY'))
            },
        },

        methods: {
            load() {
                this.saveHeight()

                this.loading = true

                this.getRowsPromise()
                    .then(response => {
                        this.rows = response.data.data

                        this.loading = false
                    })
            },

            loadPortals() {
                http.get('/api/v2/portals').then(response => {
                    this.portals = response.data.data

                    this.loadingPortals = false
                })
            },

            getRowsPromise() {
                let filters = [
                    {dimension: 'company_id', value: this.company.data.id}
                ]

                if (this.portal) {
                    filters.push({dimension: 'portal_id', value: this.portal})
                }

                return http.post('/api/v1/reports/dashboard', {
                    start: moment().subtract(8, 'year').startOf('year').format('YYYY-MM-DD'),
                    end: moment().add(1, 'year').endOf('year').format('YYYY-MM-DD'),
                    metrics: ['ad_revenue'],
                    dimensions: ['year', 'channel_id', 'channel_type', 'channel_color', 'channel_name'],
                    filters
                })
            },

            draw() {
                new Chart(this.$el.querySelector('canvas').getContext('2d'), {
                    plugins: [datalabels, totalizer],

                    type: 'bar',

                    data: {
                        labels: this.labels,
                        datasets: this.datasets
                    },

                    options: {
                        scales: {
                            xAxes: [{
                                stacked: true
                            }],
                            yAxes: [{
                                stacked: true,
                                ticks: {
                                    beginAtZero: true,
                                    callback: value => this.$options.filters.price(value)
                                }
                            }]
                        },

                        legend: {
                            position: 'bottom'
                        },

                        layout: {
                            padding: {
                                top: 25
                            }
                        },

                        plugins: {
                            datalabels: {
                                anchor: 'end',
                                align: 'end',
                                formatter: (value, ctx) => this.$options.filters.price(ctx.chart.$totalizer.totals[ctx.dataIndex]),
                                display: (ctx) => ctx.datasetIndex === ctx.chart.$totalizer.utmost
                            }
                        },

                        tooltips: {
                            callbacks: {
                                label: (t, d) => d.datasets[t.datasetIndex].label + ': ' + this.$options.filters.price(d.datasets[t.datasetIndex].data[t.index])
                            }
                        },

                        onClick: (event, elements) => {
                            if (elements.length) {
                                this.selected = this.years[elements[0]['_index']].format('YYYY')

                                this.$nextTick(() => $('#ad-revenue-breakdown-modal').modal('show'))
                            }
                        }
                    }
                });
            }
        }
    }
</script>

<style>
    .dashboard-date-widget {
        position: relative;
    }

    .dashboard-date-widget__icon {
        position: absolute;
        top: 15px;
        right: 10px;
        transform: translateY(-50%);
    }
</style>
